import {
  Pipe,
  PipeTransform,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { TranslationService } from '../appServices/translation.service';
import { Subscription, combineLatest } from 'rxjs';

@Pipe({
  name: 'translate',
  // The key can stay unchanged when its dictionary finishes loading.
  // A pure pipe would keep stale text, especially in retained Ionic views.
  pure: false,
  standalone: false,
})
export class TranslationPipe implements PipeTransform, OnDestroy {
  private static cacheGeneration = 0;
  private readonly subscription: Subscription;
  private revision = 0;
  private lastRevision = -1;
  private lastCacheGeneration = -1;
  private lastKey = '';
  private lastLanguage = '';
  private lastParams = '';
  private lastValue = '';
  private hasTransformed = false;

  constructor(
    private readonly translationService: TranslationService,
    private readonly changeDetector: ChangeDetectorRef
  ) {
    this.subscription = combineLatest([
      this.translationService.currentLanguage$,
      this.translationService.translationsLoaded$,
    ]).subscribe(() => {
      // Do not deduplicate `loaded: true`: a dictionary can finish loading
      // without changing either the current language code or this boolean.
      this.revision++;
      if (this.hasTransformed) {
        // Angular coalesces notifications and checks the affected view.
        // Never start an application-wide tick/timer/frame for each label.
        this.changeDetector.markForCheck();
      }
    });
  }

  transform(key: string, params?: { [key: string]: string | number }): string {
    const language = this.translationService.getCurrentLanguage();
    // Compare values rather than object identity: counts can mutate in place,
    // while template object literals are often recreated with the same values.
    const paramsKey = params ? JSON.stringify(params) : '';
    if (
      this.hasTransformed &&
      this.lastRevision === this.revision &&
      this.lastCacheGeneration === TranslationPipe.cacheGeneration &&
      this.lastKey === key &&
      this.lastLanguage === language &&
      this.lastParams === paramsKey
    ) {
      return this.lastValue;
    }
    this.lastRevision = this.revision;
    this.lastCacheGeneration = TranslationPipe.cacheGeneration;
    this.lastKey = key;
    this.lastLanguage = language;
    this.lastParams = paramsKey;
    this.lastValue = this.translationService.translate(key, params);
    this.hasTransformed = true;
    return this.lastValue;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // Keep the existing explicit invalidation API. Each pipe holds one binding;
  // TranslationService remains responsible for the bounded shared cache.
  static clearCache(): void {
    TranslationPipe.cacheGeneration++;
  }
}
